import { Link } from 'react-router-dom';
import { UserStatus } from '@teentack/shared';
import { HealthCard } from '@/components/HealthCard';
import { SharedPackageCard } from '@/components/SharedPackageCard';
import { SessionsCard } from '@/components/SessionsCard';
import { useAuth } from '@/auth/use-auth';
import { PageContainer } from '@/components/PageContainer';
import { Badge } from '@/components/ui/Badge';
import { buttonClasses } from '@/components/ui/button-styles';
import { ShieldCheckIcon } from '@/components/ui/Icons';
import { HeroSection } from '@/components/marketing/HeroSection';
import { FeatureStripSection } from '@/components/marketing/FeatureStripSection';
import { EarningsSection } from '@/components/marketing/EarningsSection';
import { ChoosePathSection } from '@/components/marketing/ChoosePathSection';
import { HowItWorksSection } from '@/components/marketing/HowItWorksSection';
import { PricingSection } from '@/components/marketing/PricingSection';
import { SafetySection } from '@/components/marketing/SafetySection';
import { ParentsSection } from '@/components/marketing/ParentsSection';
import { PostersSection } from '@/components/marketing/PostersSection';
import { FaqSection } from '@/components/marketing/FaqSection';
import { WaitlistSection } from '@/components/marketing/WaitlistSection';
import { ClosingCtaSection } from '@/components/marketing/ClosingCtaSection';

export function HomePage() {
  const { user, initializing, logout } = useAuth();

  if (initializing) {
    return (
      <PageContainer>
        <p className="text-slate-500">Loading…</p>
      </PageContainer>
    );
  }

  // Signed-out visitors get the marketing site. [TEEN-1, FR-M01.7]
  if (!user) {
    return (
      <>
        <HeroSection />
        <FeatureStripSection />
        <EarningsSection />
        <ChoosePathSection />
        <HowItWorksSection />
        <PricingSection />
        <SafetySection />
        <ParentsSection />
        <PostersSection />
        <FaqSection />
        <WaitlistSection />
        <ClosingCtaSection />
      </>
    );
  }

  return (
    <PageContainer>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-extrabold sm:text-4xl">
            Welcome, <span className="text-brand-gradient">{user.displayName}</span>
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge tone="accent">{user.role}</Badge>
            <Badge tone={user.status === UserStatus.ACTIVE ? 'safe' : 'sun'}>{user.status}</Badge>
          </div>
        </div>

        <div className="flex gap-2">
          <Link to="/jobs" className={buttonClasses('primary', 'sm')}>
            Find Jobs
          </Link>
          <button
            type="button"
            onClick={() => void logout()}
            className={buttonClasses('outline', 'sm')}
          >
            Sign out
          </button>
        </div>
      </div>

      {user.awaitingParentApproval && (
        <div className="mt-8 flex items-start gap-3 rounded-2xl border border-sun-200 bg-sun-50 p-5">
          <ShieldCheckIcon className="mt-0.5 size-5 shrink-0 text-sun-700" />
          <p className="text-sm leading-relaxed text-sun-700">
            <strong className="font-bold">Waiting on your guardian.</strong> Your parent or guardian
            has not approved your account yet, so opportunities are still locked.
          </p>
        </div>
      )}

      <div className="mt-8 space-y-6">
        <SessionsCard />
        <HealthCard />
        <SharedPackageCard />
      </div>
    </PageContainer>
  );
}
